import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Upload, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { api, peso, formatApiError } from "../lib/api";
import { computeDeliveryFee } from "../lib/delivery";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

const METHODS = [
  { id: "gcash", title: "GCash / InstaPay QR", desc: "Scan QR or send to 0976-141-3154", color: "#1A73E8" },
  { id: "maya", title: "Maya (PayMaya)", desc: "Send to 0976-141-3154", color: "#00D632" },
  { id: "bank", title: "Bank Transfer", desc: "Upload deposit slip", color: "#4A5D5A" },
  { id: "paypal", title: "PayPal", desc: "Upload confirmation screenshot", color: "#003087" },
  { id: "cod", title: "Cash on Delivery", desc: "Pay the driver in cash", color: "#E67E22" },
];

// Real merchant details for Arbe Minimart (Oroquieta Branch).
// GCash number and InstaPay QR belong to Jane Carla Loquero (the owner).
const MERCHANT = {
  gcash_number: "0976 141 3154",
  gcash_name: "Jane Carla Loquero",
  maya_number: "0976 141 3154",
  maya_name: "Jane Carla Loquero",
  qr_url: "/assets/payments/gcash-qr.jpg",       // InstaPay QR — works from GCash, Maya, BPI, BDO, UB, etc.
};

const PAY_INSTRUCTIONS = {
  gcash: `Scan the InstaPay QR below (works in GCash, Maya, BPI, BDO, Unionbank and most banks), OR send to GCash ${MERCHANT.gcash_number} — ${MERCHANT.gcash_name}. Then upload the receipt screenshot below.`,
  maya:  `Send payment via Maya to ${MERCHANT.maya_number} — ${MERCHANT.maya_name} (or scan the InstaPay QR below). Then upload the receipt screenshot below.`,
  bank:  "Bank transfer coming soon — please use GCash / Maya / InstaPay QR for now, or choose Cash on Delivery.",
  paypal: "PayPal coming soon — please use GCash / Maya / InstaPay QR for now, or choose Cash on Delivery.",
  cod: "No upfront payment needed. Please prepare exact cash for our driver.",
};

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Checkout() {
  const { user } = useAuth();
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [method, setMethod] = useState("gcash");
  const [proofFile, setProofFile] = useState(null);
  const [proofName, setProofName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loyalty, setLoyalty] = useState({ balance: 0 });
  const [redeemPoints, setRedeemPoints] = useState(0);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (!user) {
      toast.info("Please login to checkout");
      nav("/login?redirect=/checkout");
    } else {
      setName(user.name || "");
      setPhone(user.phone || "");
      setAddress(user.address || "");
      api.get("/loyalty/me").then((r) => setLoyalty(r.data)).catch(() => { /* noop */ });
    }
  }, [user, nav]);

  useEffect(() => { if (items.length === 0 && !submittedRef.current) nav("/cart"); }, [items, nav]);

  const deliveryFee = computeDeliveryFee(items);
  const maxRedeemable = Math.min(loyalty.balance || 0, Math.floor(subtotal));
  const safeRedeem = Math.max(0, Math.min(redeemPoints || 0, maxRedeemable));
  const discount = safeRedeem;
  const total = Math.max(0, subtotal + deliveryFee - discount);
  const earned = Math.floor(Math.max(0, subtotal - discount) / 100);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Please upload an image under 2 MB"); return; }
    const data = await fileToDataUrl(f);
    setProofFile(data);
    setProofName(f.name);
  };

  const submit = async () => {
    if (!name || !phone || !address) { toast.error("Please fill in delivery details"); return; }
    if (method !== "cod" && !proofFile) { toast.error("Please upload proof of payment"); return; }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_id: i.variant_id || null })),
        delivery_name: name,
        delivery_phone: phone,
        delivery_address: address,
        notes,
        payment_method: method,
        payment_proof: proofFile,
        redeem_points: safeRedeem,
      };
      submittedRef.current = true;
      const { data } = await api.post("/orders/checkout", payload);
      toast.success("Order placed!");
      nav(`/my-orders`);
      setTimeout(() => clear(), 50);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 page-enter">
      <div className="overline">Checkout</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1C2826] tracking-tighter">Complete your order</h1>

      <div className="grid md:grid-cols-3 gap-6 md:gap-8 mt-6 sm:mt-8">
        <div className="md:col-span-2 space-y-5 sm:space-y-6">
          {/* Delivery */}
          <div className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-6">
            <h2 className="font-heading font-bold text-lg text-[#1C2826] mb-4">Delivery details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Recipient name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" data-testid="checkout-name" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="09XX-XXX-XXXX" className="mt-1.5 h-11" data-testid="checkout-phone" />
              </div>
              <div className="sm:col-span-2">
                <Label>Full delivery address</Label>
                <Textarea value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, barangay, city, province" className="mt-1.5" rows={3} data-testid="checkout-address" />
              </div>
              <div className="sm:col-span-2">
                <Label>Notes (optional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Landmark, gate instructions..." className="mt-1.5" rows={2} data-testid="checkout-notes" />
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-6">
            <h2 className="font-heading font-bold text-lg text-[#1C2826] mb-4">Payment method</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {METHODS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMethod(m.id)}
                  className={`text-left rounded-xl border-2 p-4 transition-all ${method === m.id ? "border-[#2F5233] bg-[#F1F5EF]" : "border-[#E1E5DF] bg-white hover:border-[#2F5233]/50"}`}
                  data-testid={`payment-${m.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-heading font-semibold text-[#1C2826]">{m.title}</div>
                    {method === m.id && <CheckCircle2 className="h-5 w-5 text-[#2F5233]" />}
                  </div>
                  <div className="text-xs text-[#738380] mt-1">{m.desc}</div>
                </button>
              ))}
            </div>

            <div className="mt-4 p-4 bg-[#F9F9F7] border border-[#E1E5DF] rounded-xl text-sm text-[#4A5D5A]">
              {PAY_INSTRUCTIONS[method]}
            </div>

            {/* InstaPay QR block — shown for GCash and Maya.
                InstaPay QR is a universal QR that works from GCash, Maya,
                and every major PH bank app (BPI, BDO, UB, Metrobank etc).  */}
            {(method === "gcash" || method === "maya") && (
              <div className="mt-4 grid sm:grid-cols-[auto,1fr] gap-4 items-start p-4 bg-white border-2 border-[#2F5233]/20 rounded-xl" data-testid="gcash-qr-block">
                <img
                  src={MERCHANT.qr_url}
                  alt="InstaPay QR — scan with GCash, Maya, or your bank app"
                  className="w-40 h-40 sm:w-48 sm:h-48 object-contain bg-white rounded-lg border border-[#E1E5DF]"
                  data-testid="gcash-qr-image"
                />
                <div className="space-y-2 text-sm">
                  <div className="font-heading font-bold text-[#1C2826] text-base">Scan to pay (InstaPay)</div>
                  <div className="text-[#4A5D5A]">
                    Works from <strong>GCash, Maya, BPI, BDO, Unionbank</strong> and most PH bank apps.
                  </div>
                  <div className="pt-1 border-t border-[#E1E5DF]" />
                  <div className="text-[#4A5D5A]">Or send manually to:</div>
                  <div className="flex items-center gap-2">
                    <div className="font-heading font-bold text-[#1C2826] text-lg tracking-wide" data-testid="gcash-number">{MERCHANT.gcash_number}</div>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard?.writeText(MERCHANT.gcash_number.replace(/\s/g, ""));
                        toast.success("GCash number copied");
                      }}
                      className="text-xs px-2 py-1 rounded-md bg-[#2F5233] text-white hover:bg-[#1F3D24]"
                      data-testid="gcash-copy-number"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="text-[#4A5D5A]">Account name: <strong>{MERCHANT.gcash_name}</strong></div>
                </div>
              </div>
            )}

            {method !== "cod" && (
              <div className="mt-4">
                <Label>Proof of payment (image)</Label>
                <label className="mt-2 block border-2 border-dashed border-[#E1E5DF] rounded-xl p-6 text-center cursor-pointer hover:border-[#2F5233] transition-colors" data-testid="upload-proof-label">
                  <Upload className="h-6 w-6 text-[#738380] mx-auto" />
                  <div className="text-sm text-[#4A5D5A] mt-2">{proofName || "Click to upload screenshot"}</div>
                  <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="upload-proof-input" />
                </label>
                {proofFile && (
                  <img src={proofFile} alt="proof preview" className="mt-3 rounded-xl max-h-48 border border-[#E1E5DF]" />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="md:col-span-1">
          <div className="bg-white border border-[#E1E5DF] rounded-2xl p-5 sm:p-6 md:sticky md:top-24">
            <h2 className="font-heading font-bold text-lg text-[#1C2826]">Summary</h2>
            <div className="mt-4 space-y-2 max-h-60 overflow-auto">
              {items.map((i) => (
                <div key={i.key || i.product_id} className="flex justify-between text-sm">
                  <span className="text-[#4A5D5A] line-clamp-1 mr-2">{i.quantity}× {i.name}{i.variant_label ? ` — ${i.variant_label}` : ""}</span>
                  <span className="font-semibold">{peso(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-[#E1E5DF] space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span className="font-semibold">{peso(subtotal)}</span></div>
              <div className="flex justify-between"><span>Delivery</span><span className="font-semibold">{peso(deliveryFee)}</span></div>
              {discount > 0 && (
                <div className="flex justify-between text-[#C62828]" data-testid="checkout-discount-row">
                  <span>Suki points discount</span>
                  <span className="font-semibold">-{peso(discount)}</span>
                </div>
              )}
            </div>

            {/* Suki Loyalty redemption */}
            {loyalty.balance > 0 && (
              <div className="mt-4 p-3 bg-[#F1F5EF] border border-[#D5E0CD] rounded-xl" data-testid="suki-redeem-block">
                <div className="flex items-center gap-2 text-[#2F5233]">
                  <Sparkles className="h-4 w-4" />
                  <span className="text-xs font-bold uppercase tracking-wider">Suki Loyalty</span>
                </div>
                <div className="text-xs text-[#4A5D5A] mt-1">
                  You have <span className="font-bold text-[#2F5233]">{loyalty.balance} points</span> · 1 pt = ₱1 off
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={maxRedeemable}
                    value={redeemPoints}
                    onChange={(e) => setRedeemPoints(Math.max(0, Math.min(maxRedeemable, parseInt(e.target.value) || 0)))}
                    placeholder="0"
                    className="h-9 text-sm"
                    data-testid="suki-redeem-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setRedeemPoints(maxRedeemable)}
                    className="border-[#2F5233] text-[#2F5233] hover:bg-[#2F5233] hover:text-white text-xs whitespace-nowrap"
                    data-testid="suki-redeem-max"
                  >
                    Use max ({maxRedeemable})
                  </Button>
                </div>
              </div>
            )}

            {earned > 0 && (
              <div className="mt-2 text-[11px] text-[#2F5233] flex items-center gap-1.5" data-testid="suki-earn-preview">
                <Sparkles className="h-3 w-3" />
                You&apos;ll earn <span className="font-bold">{earned} Suki points</span> from this order
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-[#E1E5DF] flex justify-between items-center">
              <span className="font-heading font-semibold">Total</span>
              <span className="font-heading font-extrabold text-2xl text-[#2F5233]">{peso(total)}</span>
            </div>
            <Button disabled={loading} onClick={submit} className="w-full mt-5 h-11 bg-[#E67E22] hover:bg-[#D35400] text-white rounded-full font-semibold" data-testid="place-order-btn">
              {loading ? "Placing order..." : "Place order"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
